import { campaignsClient } from 'clients/CampaignsClient/CampaignsClient';
import {
  CandidateOutreachSendMethod,
  NotifyCandidateData,
  NotifyCandidateMetadata,
} from 'clients/CampaignsClient/CampaignsClient.types';
import { userClient } from 'clients/UserClient/UserClient';
import { encodeBase64 } from 'helpers/base64';
import { isAxiosError } from 'helpers/clientHelpers';
import { usePageContext } from 'hooks/shared/usePageContext';
import { PageContext } from 'hooks/shared/usePageContext.types';
import { useContext } from 'react';
import { CANDIDATE_STATUS } from 'services/candidates';
import { CandidateHistoryAction } from 'services/candidates/types';
import { LocalizationContext } from 'shared/legacy/contexts/LocalizationContext/LocalizationContext';
import { SelectedCampaignContext } from 'shared/legacy/contexts/SelectedCampaignContext';
import { SnackbarContext } from 'shared/legacy/contexts/SnackbarContext/SnackbarContext';
import { UserContext } from 'shared/legacy/contexts/UserContext/UserContext';
import { BadRequestException } from 'shared/legacy/exceptions/BadRequestException';
import { useBrowserExtension, useFeatureToggles } from 'shared/legacy/hooks';
import { useIndeedConfig } from 'shared/legacy/hooks/useBrowserExtension/indeed/useIndeedConfig';
import { useIndeedReplyMessageMutation } from 'shared/legacy/hooks/useBrowserExtension/indeed/useIndeedReplyMessageMutation';
import { useIndeedSendMessageMutation } from 'shared/legacy/hooks/useBrowserExtension/indeed/useIndeedSendMessageMutation';
import { useSendLinkedinMessageMutation } from 'shared/legacy/hooks/useBrowserExtension/linkedin/useSendLinkedinMessageMutation';
import {
  addCalendarInvitationSentToCandidateHistoryQueryData,
  candidateHistoryQuerySetValue,
} from 'shared/legacy/hooks/useBrowserExtension/useCandidateHistoryQuery.setValue';
import { useCreateCustomLogMutation } from 'shared/legacy/hooks/useCreateCustomLogMutation';
import { updateCentralInboxSidebar } from 'shared/legacy/hooks/useInfiniteConversationsQuery';
import { useContextSelector } from 'use-context-selector';
import { sharedUtils } from 'utils';
import { ContactActionResult, ContactHookCallbackParams } from '../types';
import { followUpScheduleInMinutes } from './nowPlusXMinutes';
import { useCandidateAction, WrappedHookParams } from './useCandidateAction';
import { isAReplyMessage } from './useContact.utils';

const TWO_DAYS_IN_MINUTES = 2 * 24 * 60;
const FIVE_DAYS_IN_MINUTES = 5 * 24 * 60;

function emailBodyContainsSchedulerLink(body: string, schedulerLink: string): boolean {
  const normalizedBody = body.replace(/&amp;/g, '&');
  const link = schedulerLink.trim();
  if (!link) return false;
  if (normalizedBody.includes(link)) return true;
  const withoutTrailingSlash = link.replace(/\/$/, '');
  if (normalizedBody.includes(withoutTrailingSlash) || normalizedBody.includes(withoutTrailingSlash + '/')) return true;
  try {
    if (normalizedBody.includes(decodeURIComponent(link))) return true;
  } catch {
    // ignore malformed URI
  }
  return false;
}

function sanitizeText(value: string = '') {
  const allowedCharacters = /^[a-zA-Z0-9\s&*():;'"!@\?\{\}]*$/;

  if (!allowedCharacters.test(value)) {
    return value.replace(/[^a-zA-Z0-9\s&*():;'"!@\?\{\}]/g, '');
  }

  return value;
}

export const useContact = (params: WrappedHookParams) => {
  const campaignName = useContextSelector(SelectedCampaignContext, (s) => s.campaign?.name ?? '');
  const { createSnackbar } = useContext(SnackbarContext);
  const { dictionary, language } = useContext(LocalizationContext);
  const { email: indeedLicenseEmail } = useIndeedConfig();
  const { currentUser, mailSignOff } = useContext(UserContext);
  const {
    sendLinkedinConnectionRequest,
    sendLinkedinProMessage,
    existsLinkedinConversationWithCandidate,
    existsLinkedinInmailConversationWithCandidate,
  } = useBrowserExtension();
  const pageContext = usePageContext();

  const { getFeatureToogleValue } = useFeatureToggles();

  const indeedSendMessageMutation = useIndeedSendMessageMutation();
  const indeedReplyMessageMutation = useIndeedReplyMessageMutation();

  const sendLinkedinMessage = useSendLinkedinMessageMutation();
  const createCustomLogMutation = useCreateCustomLogMutation();

  return useCandidateAction<ContactHookCallbackParams, ContactActionResult>(
    async ({ candidate, data, sendMethod, extras }) => {
      let thread_id = undefined;
      let metadata: NotifyCandidateMetadata | undefined = undefined;

      if (sendMethod === 'linkedin') {
        if (extras?.linkedinMarkAsContacted) {
          if (candidate.status === CANDIDATE_STATUS.MATCHED) return;

          await campaignsClient.createOrUpdateCampaignCandidate({
            campaignId: candidate.campaignId,
            esPersonId: candidate.es_person_id,
            status: 'CONTACTED',
            skipContactedLogCreation: true,
          });

          return;
        }

        const candidateConversation = await existsLinkedinConversationWithCandidate(
          candidate.es_person_id_without_platform,
        );

        if (candidateConversation !== undefined) {
          const response = await sendLinkedinMessage.mutateAsync({
            text: sharedUtils.htmlToPlainText(data.body ?? ''),
            candidateConversation,
          });

          if (response.status === 200) {
            thread_id = encodeBase64(`${currentUser?.id}-${candidate.es_person_id}`);
          }
        } else {
          const response = await sendLinkedinConnectionRequest({
            ...(data.body !== undefined && {
              customMessage: sharedUtils.htmlToPlainText(data.body),
            }),
            inviteeProfileUrn: `urn:li:fsd_profile:${candidate.es_person_id_without_platform}`,
          });

          if (response.status === 200) {
            thread_id = encodeBase64(`${currentUser?.id}-${candidate.es_person_id}`);
          }
        }
      } else if (sendMethod === 'indeed' && currentUser) {
        if (extras?.linkedinMarkAsContacted) {
          if (candidate.status === CANDIDATE_STATUS.MATCHED) return;

          await campaignsClient.createOrUpdateCampaignCandidate({
            campaignId: candidate.campaignId,
            esPersonId: candidate.es_person_id,
            status: 'CONTACTED',
          });

          return;
        }

        const tenant = await userClient.getTenantCurrentUser();

        if (extras?.isIndeedReply && extras?.indeedMetadata) {
          await indeedReplyMessageMutation.mutateAsync({
            message: data.body as string,
            conversationId: extras.indeedMetadata?.conversation_id,
            jobSeekerKey: extras.indeedMetadata?.job_seeker_key,
          });
        } else {
          if (!extras?.indeedProjectId) throw TypeError('indedeProjectId must be provided');

          metadata = {
            job_info: {
              jobTitle: extras.indeedJob?.name,
            },
          };

          await indeedSendMessageMutation.mutateAsync({
            candidateId: candidate.es_person_id_without_platform,
            senderName: mailSignOff.fullName as string,
            senderCompany: `${tenant.data.name}`,
            jobTitle: data.subject as string,
            message: data.body as string,
            projectKey: extras.indeedProjectId,
            job: extras.indeedJob,
          });
        }

        const sanitizedName = candidate.name.split(' ').join('');
        const encodedName = encodeURIComponent(sanitizedName).replace(/%([0-9A-F]{2})/g, (_, p1) =>
          String.fromCharCode(parseInt(p1, 16)),
        );
        const base64Name = btoa(encodedName);

        thread_id = `${tenant.data.id}_${base64Name}`;
      } else if (sendMethod === 'linkedin_inmail') {
        const threadId = await existsLinkedinInmailConversationWithCandidate(candidate.es_person_id_without_platform);

        await sendLinkedinProMessage({
          candidate_id: candidate.es_person_id_without_platform,
          subject: data.subject as string,
          body: data.body as string,
          signature: mailSignOff.fullName as string,
          mailThread: threadId,
        });

        thread_id = encodeBase64(`${currentUser?.id}-${candidate.es_person_id}`);
      }

      const notifyCandidateData: NotifyCandidateData = {
        message: data.body,
        sign_off: sendMethod !== 'werknl' ? data.signOff || '' : mailSignOff?.fullName,
        subject: sendMethod === 'werknl' ? sanitizeText(data.subject) : data.subject,
        email: data.email,
        phone_number: sendMethod === 'werknl' ? data.phoneNumber?.replace(/\D/g, '') : data.phoneNumber,
        website: data.website,
        status: 'success',
        provider_thread_id: thread_id,
        metadata: metadata,
        is_ai_leveraged: extras?.isAiLeveraged,
        ...(extras?.isAiLeveraged && extras?.outreachOrigin ? { origin: extras.outreachOrigin } : {}),
        ...(extras?.shouldScheduleFollowUp
          ? {
              schedule_follow_up_to: [
                followUpScheduleInMinutes(
                  getFeatureToogleValue((f) => f.FIRST_FOLLOW_UP_IN_MINUTES),
                  TWO_DAYS_IN_MINUTES,
                ),
                followUpScheduleInMinutes(
                  getFeatureToogleValue((f) => f.SECOND_FOLLOW_UP_IN_MINUTES),
                  FIVE_DAYS_IN_MINUTES,
                ),
              ].filter((maybeDate) => !!maybeDate),
            }
          : {}),
      };

      const attachments =
        sendMethod === 'email' && extras?.attachments && extras.attachments.length > 0 ? extras.attachments : undefined;

      if (extras?.emailMarkAsContacted) {
        await campaignsClient.createOrUpdateCampaignCandidate({
          campaignId: candidate.campaignId,
          esPersonId: candidate.es_person_id,
          status: CANDIDATE_STATUS.CONTACTED,
        });

        return;
      }

      const notifyResponse = await campaignsClient.notifyCandidate({
        campaign_id: candidate.campaignId,
        candidate_id: candidate.es_person_id,
        send_method: sendMethod,
        data: notifyCandidateData,
        attachments,
      });

      return { notificationId: notifyResponse.data.candidate_notification_id };
    },
    {
      onSuccess: ({
        fcParams: { candidate, sendMethod, data, extras, options: { waitBeforeRemoveFromList } = {} },
        gtm,
      }) => {
        updateCentralInboxSidebar({ esPersonId: candidate.es_person_id });
        gtm.contactCandidate(candidate.campaignId, candidate.es_person_id, pageContext, isAReplyMessage(candidate));
        gtm.contactSentMessage(
          candidate.campaignId,
          candidate.es_person_id,
          sendMethod,
          pageContext,
          isAReplyMessage(candidate),
        );

        if (candidate.status !== CANDIDATE_STATUS.CONTACTED) {
          createSnackbar(dictionary.candidateIsMovedTo(candidate.firstName, dictionary.contacted), {
            variant: 'success',
          });
        }

        return async ({ candidateOrchestrator: co, pageContext }) => {
          const outreachSendMethod = sendMethod as CandidateOutreachSendMethod;
          let metadata = {};

          switch (outreachSendMethod) {
            case CandidateOutreachSendMethod.Indeed:
              metadata = {
                license_email_address: indeedLicenseEmail,
                job_info: {
                  jobTitle: extras?.indeedJob?.name,
                },
              };
              break;

            default:
              break;
          }

          candidateHistoryQuerySetValue({
            language,
            candidate,
            currentUser,
            campaignName,
            content: { message: data.body, signoff: data.signOff, subject: data.subject },
            sendMethod: outreachSendMethod,
            metadata,
          });

          const sentEmailHadSchedulerLink =
            sendMethod === 'email' &&
            extras?.schedulerLink != null &&
            extras.schedulerLink !== '' &&
            emailBodyContainsSchedulerLink(data.body ?? '', extras.schedulerLink);

          if (sentEmailHadSchedulerLink) {
            createCustomLogMutation.mutate({
              es_person_id: candidate.es_person_id,
              campaign: candidate.campaignId,
              source: candidate.activeJobBoard ?? '',
              action: CandidateHistoryAction.CalendarInterviewInvitationSent,
              contacted_channel: CandidateOutreachSendMethod.Email,
            });
            addCalendarInvitationSentToCandidateHistoryQueryData({ candidate, currentUser, campaignName, language });

            if (candidate.status !== CANDIDATE_STATUS.MATCHED) {
              await campaignsClient.createOrUpdateCampaignCandidate({
                campaignId: candidate.campaignId,
                esPersonId: candidate.es_person_id,
                status: CANDIDATE_STATUS.MATCHED,
              });
            }
          }

          if (pageContext === PageContext.Campaign) {
            if (candidate.status === CANDIDATE_STATUS.NEW) {
              co.campaign.decreaseStatOf.currentJobBoard();
            }

            if (!candidate.reasons.includes('contacted')) {
              co.campaign.update.labels.add('contacted');
            }

            if ([CANDIDATE_STATUS.MATCHED, CANDIDATE_STATUS.CONTACTED].every((s) => s !== candidate.status)) {
              const handeRemoveFromList = () => {
                co.campaign.update.locally({ status: CANDIDATE_STATUS.CONTACTED });
                co.campaign.remove.fromCurrentList();
                co.campaign.reset.contactList();
                co.campaign.decreaseStatOf.currentList();
                co.campaign.increaseStatOf.contactList();
              };

              if (waitBeforeRemoveFromList) {
                waitBeforeRemoveFromList().then(({ removeFromList }) => {
                  if (removeFromList) {
                    handeRemoveFromList();
                  }
                });
              } else {
                handeRemoveFromList();
              }
            }
          } else if (pageContext === PageContext.RatedCandidates) {
            if (!candidate.reasons.includes('contacted')) {
              co.ratedCandidates.update.labels.add('contacted');
            }

            if ([CANDIDATE_STATUS.MATCHED, CANDIDATE_STATUS.CONTACTED].every((s) => s !== candidate.status)) {
              const handeRemoveFromList = () => {
                co.ratedCandidates.update.locally({ status: CANDIDATE_STATUS.CONTACTED });
                co.ratedCandidates.remove.fromCurrentList();
                co.ratedCandidates.reset.contactList();
                co.ratedCandidates.decreaseStatOf.currentList();
                co.ratedCandidates.increaseStatOf.contactList();
              };

              if (waitBeforeRemoveFromList) {
                waitBeforeRemoveFromList().then(({ removeFromList }) => {
                  if (removeFromList) {
                    handeRemoveFromList();
                  }
                });
              } else {
                handeRemoveFromList();
              }
            }
          } else if (pageContext === PageContext.Shared || pageContext === PageContext.Candidate) {
            if (!candidate.reasons.includes('contacted')) {
              co.share.update.labels.add('contacted');
            }

            if (candidate.status !== CANDIDATE_STATUS.MATCHED) {
              co.share.moveCandidateTo.contactList();
            }
          }
        };
      },
      onError: ({ fcParams: { candidate, sendMethod }, gtm }) => {
        gtm.contactCandidateFailed(candidate.campaignId, candidate.es_person_id, sendMethod);

        return ({ data }) => {
          if (isAxiosError(data) && data.response?.status === 400 && sendMethod === 'email') {
            throw new BadRequestException();
          }

          throw data;
        };
      },
    },
  )(params);
};
